import React from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  FaCheck,
  FaDownload,
  FaHome,
  FaRocket,
  FaCrown,
  FaFilePdf,
} from "react-icons/fa";
import jsPDF from "jspdf";

const ThankYouPage = () => {
  const location = useLocation();

  const paymentData = location.state || {
    fullName: "John Doe",
    companyName: "Demo Company Pvt Ltd",
    mobileNumber: "+91 9876543210",
    emailAddress: "demo@gmail.com",
    gstNumber: "22AAAAA0000A1Z5",
    numberOfUsers: 5,
    planName: "Business Plan",
    subtotal: 4995,
    gstAmount: 899.1,
    total: 5894.1,
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();

    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, 220, 40, "F");

    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text("Payment Receipt", 20, 28);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(13);

    const details = [
      `Full Name: ${paymentData.fullName}`,
      `Company: ${paymentData.companyName}`,
      `Mobile: ${paymentData.mobileNumber}`,
      `Email: ${paymentData.emailAddress}`,
      `GST: ${paymentData.gstNumber || "N/A"}`,
      `Plan: ${paymentData.planName}`,
      `Users: ${paymentData.numberOfUsers}`,
      `Subtotal: ₹${paymentData.subtotal}`,
      `GST (18%): ₹${paymentData.gstAmount}`,
      `Total: ₹${paymentData.total}`,
    ];

    let y = 65;
    details.forEach((item) => {
      doc.text(item, 20, y);
      y += 10;
    });

    doc.setFillColor(16, 185, 129);
    doc.roundedRect(15, y + 15, 180, 28, 5, 5, "F");

    doc.setFontSize(15);
    doc.setTextColor(255, 255, 255);
    doc.text("Payment Successfully Completed", 35, y + 32);

    doc.save("payment-receipt.pdf");
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 flex items-center justify-center px-4 py-12">
      
      {/* Background Glows */}
      <motion.div
        animate={{ scale: [1, 1.4, 1], opacity: [0.25, 0.5, 0.25] }}
        transition={{ repeat: Infinity, duration: 9 }}
        className="absolute top-[-80px] left-[-80px] w-[380px] h-[380px] rounded-full bg-blue-500 blur-[120px]"
      />
      <motion.div
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.3, 0.55, 0.3] }}
        transition={{ repeat: Infinity, duration: 11 }}
        className="absolute bottom-[-100px] right-[-100px] w-[380px] h-[380px] rounded-full bg-purple-600 blur-[120px]"
      />

      {/* Floating Icons */}
      {[...Array(7)].map((_, i) => (
        <motion.div
          key={i}
          animate={{ y: [0, -25, 0], rotate: [0, 12, -12, 0] }}
          transition={{ repeat: Infinity, duration: 5 + i * 0.4 }}
          className="absolute text-white/10"
          style={{
            top: `${12 + i * 11}%`,
            left: `${6 + i * 13}%`,
            fontSize: i % 2 === 0 ? "2.8rem" : "2.2rem",
          }}
        >
          {i % 2 === 0 ? <FaRocket /> : <FaCrown />}
        </motion.div>
      ))}

      {/* Main Card */}
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, type: "spring", stiffness: 110 }}
        className="relative z-10 w-full max-w-3xl"
      >
        <div className="backdrop-blur-3xl bg-white/10 border border-white/20 rounded-3xl overflow-hidden shadow-2xl">
          
          {/* Top Success Section */}
          <div className="relative bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-6 py-14 text-center">
            <div className="absolute inset-0 bg-white/10" />

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 180, delay: 0.2 }}
              className="w-28 h-28 mx-auto rounded-full bg-white/20 border border-white/30 backdrop-blur-xl flex items-center justify-center shadow-xl"
            >
              <FaCheck className="text-white text-6xl" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6 text-4xl md:text-5xl font-black text-white tracking-tight"
            >
              Payment Successful
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-3 text-lg text-white/90"
            >
              Your subscription has been activated successfully 🎉
            </motion.p>
          </div>

          {/* Content Area */}
          <div className="p-6 md:p-10">
            
            {/* User Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {[
                { label: "Customer Name", value: paymentData.fullName },
                { label: "Company Name", value: paymentData.companyName },
                { label: "Selected Plan", value: paymentData.planName },
                { label: "Number of Users", value: paymentData.numberOfUsers },
                { label: "Email Address", value: paymentData.emailAddress },
                { label: "Mobile Number", value: paymentData.mobileNumber },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-xl"
                >
                  <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">
                    {item.label}
                  </p>
                  <h3 className="text-lg font-semibold text-white break-words">
                    {item.value}
                  </h3>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-7 md:p-8 text-white mb-10 shadow-inner"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Order Summary</h2>
                <FaFilePdf className="text-4xl opacity-70" />
              </div>

              <div className="space-y-3 text-[15px]">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{paymentData.subtotal}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST (18%)</span>
                  <span>₹{paymentData.gstAmount}</span>
                </div>
                <div className="border-t border-white/30 pt-4 flex justify-between text-xl font-bold">
                  <span>Total Paid</span>
                  <span>₹{paymentData.total}</span>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={handleDownloadPDF}
                className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 font-semibold text-lg flex items-center justify-center gap-3 shadow-lg"
              >
                <FaDownload className="group-hover:animate-bounce" />
                Download PDF Receipt
              </motion.button>

              <Link to="/" className="flex-1">
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  className="w-full py-4 rounded-2xl bg-white text-gray-900 font-semibold text-lg flex items-center justify-center gap-3 shadow-lg"
                >
                  <FaHome />
                  Go To Home
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ThankYouPage;